import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Box, Typography, Table, TableBody, TableHead, TableRow, TableCell } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonLoading from '../../../../form-components/CommonLoading';
import CommonSelect from '../../../../form-components/CommonSelect';
import { getExpireUpdateHistory, getStopHistory } from '../../../../api/member-course-history';
import {
  EXPIRE_ACTION_LABELS,
  STOP_ACTION_LABELS,
  toChangeContent,
  toMemo,
} from '../../UserCourseHistory/UserCourseHistoryTable';

const ModalContainer = styled(Box)({
  width: 'min(1120px, 94vw)',
  maxHeight: '84vh',
  position: 'absolute',
  top: '50%',
  left: '50%',
  background: '#fff',
  borderRadius: '8px',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ModalHeader = styled(Box)({
  height: '56px',
  background: '#262626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 32px',
  flexShrink: 0,
});

const ModalTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#fff',
});

const CloseButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { color: '#fff', fontSize: '24px' },
});

const ModalContent = styled(Box)({
  padding: '24px 32px 8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  overflowY: 'auto',
});

const ProductName = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#262626',
  padding: '10px 14px',
  background: '#F7F8F9',
  borderRadius: '6px',
});

const FilterBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

const FilterLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#5C6470',
  whiteSpace: 'nowrap',
});

// 모달을 키워도 좁은 화면에서는 표가 넘친다. 그때만 가로 스크롤.
const TableWrap = styled(Box)({
  overflowX: 'auto',
});

const TotalCount = styled(Typography)({
  fontSize: '13px',
  color: '#8C8C8C',
  marginLeft: 'auto',
});

const HeaderCell = styled(TableCell)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#5C6470',
  background: '#F7F8F9',
  borderBottom: '1px solid #E0E4EA',
  whiteSpace: 'nowrap',
});

const BodyCell = styled(TableCell)({
  fontSize: '13px',
  color: '#262626',
  borderBottom: '1px solid #EEF1F5',
  // 값이 길어도 두 줄로 흐르지 않게 한 줄 + 말줄임. 전체 값은 title 로 본다.
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 0,
});

const ButtonWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 32px 28px',
  flexShrink: 0,
});

// 카드 종류 → 이력 API 의 category
const CATEGORY = { course: 'TICKET', pass: 'PASS', trial: 'TRIAL' };

const toDateTime = value => (value ? dayjs(value).format('YYYY.MM.DD HH:mm') : '-');
const toDate = value => (value ? dayjs(value).format('YYYY.MM.DD') : '-');

// 중지 계열은 before/after 컬럼이 아니라 중지일·해제일·일수를 쓴다.
const isStopAction = actionType => !!STOP_ACTION_LABELS[actionType];

const actionLabel = actionType =>
  EXPIRE_ACTION_LABELS[actionType] ?? STOP_ACTION_LABELS[actionType] ?? actionType ?? '-';

const describe = row => {
  if (isStopAction(row?.actionType)) {
    const days = row?.stoppedDays != null ? ` (${row.stoppedDays}일)` : '';
    return `${toDate(row?.stoppedDate)} ~ ${toDate(row?.stoppedEndDate)}${days}`;
  }
  return toChangeContent(row);
};

/**
 * 회원권 1건에 일어난 변경 이벤트 목록.
 * 정보수정(기간·횟수·예약설정·이용지점 등)과 중지/중지해제를 한 줄로 합쳐 최신순으로 보여준다.
 * 예약·수업 내역은 별도 화면이라 여기 포함하지 않는다.
 */
const TicketChangeHistoryModal = ({ open, target, memberId, branchId, token, onClose }) => {
  const { t } = useTranslation();

  const category = CATEGORY[target?.kind];
  const ticketId = target?.ticketId;
  const enabled = !!open && !!token && !!branchId && !!ticketId;

  const query = qs.stringify({
    branchId,
    memberId,
    category,
    memberTicketId: ticketId,
    page: 1,
    limit: 200,
  });

  // 방금 한 변경이 바로 보여야 하는 화면이라 전역 staleTime(30초) 캐시를 타지 않고 열 때마다 새로 받는다.
  const { data: infoList, isLoading: infoLoading } = useQuery({
    queryKey: ['ticketChangeHistory', 'info', category, ticketId],
    queryFn: () => getExpireUpdateHistory(query, token),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: stopList, isLoading: stopLoading } = useQuery({
    queryKey: ['ticketChangeHistory', 'stop', category, ticketId],
    queryFn: () => getStopHistory(query, token),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const rows = useMemo(() => {
    const merged = [...(infoList?.content ?? []), ...(stopList?.content ?? [])];
    return merged.sort((a, b) => new Date(b?.regDt ?? 0) - new Date(a?.regDt ?? 0));
  }, [infoList, stopList]);

  // 변경 종류 필터 — 실제 내역에 있는 종류만 선택지로 낸다(회원 단위 이력 화면과 같은 방식).
  const [actionFilter, setActionFilter] = useState('');

  useEffect(() => {
    setActionFilter('');
  }, [ticketId]);

  const actionOptions = useMemo(
    () =>
      [...new Set(rows.map(row => row?.actionType).filter(Boolean))].map(actionType => ({
        value: actionType,
        label: actionLabel(actionType),
      })),
    [rows],
  );

  const filteredRows = useMemo(
    () => (actionFilter ? rows.filter(row => row?.actionType === actionFilter) : rows),
    [rows, actionFilter],
  );

  if (!open || !target) return null;

  const isLoading = infoLoading || stopLoading;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{t('변경 내역')}</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <ProductName>{target.productName}</ProductName>

          {isLoading ? (
            <CommonLoading minHeight={220} />
          ) : (
            <>
              <FilterBar>
                <FilterLabel>{t('변경 종류')}</FilterLabel>
                <CommonSelect
                  value={actionFilter}
                  onChange={e => setActionFilter(e.target.value)}
                  emptyLabel={t('전체')}
                  options={actionOptions}
                  width={200}
                />
                <TotalCount>총 {filteredRows.length.toLocaleString()}건</TotalCount>
              </FilterBar>
              <TableWrap>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: '900px' }}>
                <colgroup>
                  <col style={{ width: '140px' }} />
                  {/* 변경 종류 — 분류값이라 잘리면 뜻을 잃는다. 최장 라벨이 한 줄로 들어갈 폭 */}
                  <col style={{ width: '170px' }} />
                  {/* 변경 내용 — 서술형이라 길다. 남는 폭을 쓰고 넘치면 말줄임(마우스 오버로 전체) */}
                  <col />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '110px' }} />
                  <col style={{ width: '140px' }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <HeaderCell align="center">{t('실행일')}</HeaderCell>
                    <HeaderCell align="center">{t('변경 종류')}</HeaderCell>
                    <HeaderCell align="center">{t('변경 내용')}</HeaderCell>
                    <HeaderCell align="center">{t('메모')}</HeaderCell>
                    <HeaderCell align="center">{t('관리 담당자')}</HeaderCell>
                    <HeaderCell align="center">{t('처리지점')}</HeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <BodyCell colSpan={6} align="center" sx={{ padding: '32px 0', color: '#8C8C8C' }}>
                        {t('내역이 없습니다')}
                      </BodyCell>
                    </TableRow>
                  ) : (
                    filteredRows.map(row => (
                      <TableRow key={`${row?.category}-${row?.id}`}>
                        <BodyCell align="center">{toDateTime(row?.regDt)}</BodyCell>
                        <BodyCell align="center" sx={{ whiteSpace: 'nowrap', textOverflow: 'clip' }}>
                          {actionLabel(row?.actionType)}
                        </BodyCell>
                        <BodyCell align="center" title={describe(row)}>{describe(row)}</BodyCell>
                        <BodyCell
                          align="center"
                          title={isStopAction(row?.actionType) ? row?.stopMemo ?? '' : toMemo(row)}>
                          {isStopAction(row?.actionType) ? row?.stopMemo ?? '-' : toMemo(row)}
                        </BodyCell>
                        <BodyCell align="center">{row?.regName ?? '-'}</BodyCell>
                        <BodyCell align="center">{row?.processedBranchName ?? '-'}</BodyCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </TableWrap>
            </>
          )}
        </ModalContent>

        <ButtonWrapper>
          <CommonModalButton variant="cancel" onClick={onClose}>
            {t('닫기')}
          </CommonModalButton>
        </ButtonWrapper>
      </ModalContainer>
    </Modal>
  );
};

export default TicketChangeHistoryModal;
